import { useCallback, useEffect, useState } from "react";

export type VideoPlayback = {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  muted: boolean;
  togglePlay: () => void;
  toggleMute: () => void;
  /** Seek to an absolute time (seconds). */
  seek: (time: number) => void;
};

/**
 * Playback state + controls for the viewer's custom video UI, derived from the
 * element's OWN events (`play`/`pause`/`timeupdate`/…) — never an assumed
 * initial state, so it can't drift out of sync (e.g. when iOS blocks unmuted
 * autoplay and the video is actually paused).
 *
 * Exists because the viewer's `<video>` carries no native `controls`: the
 * element sits inside the zoom transform, and native controls under a CSS
 * transform scale with the video AND get duplicated by iOS's system (glass)
 * control overlay. The custom controls render OUTSIDE the transform and drive
 * the element through this hook.
 */
export function useVideoPlayback(el: HTMLVideoElement | null): VideoPlayback {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);

  useEffect(() => {
    if (!el) {
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      setMuted(false);
      return;
    }
    // Sync from the element's live state (it may already be playing/muted by
    // the time it's handed over — e.g. the muted-autoplay retry ran first).
    setIsPlaying(!el.paused);
    setCurrentTime(el.currentTime || 0);
    setDuration(Number.isFinite(el.duration) ? el.duration : 0);
    setMuted(el.muted);

    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onTime = () => setCurrentTime(el.currentTime || 0);
    const onDuration = () =>
      setDuration(Number.isFinite(el.duration) ? el.duration : 0);
    const onVolume = () => setMuted(el.muted);

    el.addEventListener("play", onPlay);
    el.addEventListener("pause", onPause);
    el.addEventListener("ended", onPause);
    // Swapping src on a REUSED element (cached→cached swipe) pauses it
    // SILENTLY — the media load algorithm sets paused=true with no pause
    // event; emptied is the only reliable signal it emits.
    el.addEventListener("emptied", onPause);
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("durationchange", onDuration);
    el.addEventListener("loadedmetadata", onDuration);
    el.addEventListener("volumechange", onVolume);
    return () => {
      el.removeEventListener("play", onPlay);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("ended", onPause);
      el.removeEventListener("emptied", onPause);
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("durationchange", onDuration);
      el.removeEventListener("loadedmetadata", onDuration);
      el.removeEventListener("volumechange", onVolume);
    };
  }, [el]);

  const togglePlay = useCallback(() => {
    if (!el) {
      return;
    }
    if (el.paused) {
      const attempt = el.play();
      if (attempt && typeof attempt.catch === "function") {
        attempt.catch(() => {
          // Blocked (rare — this runs on a user gesture); state stays paused.
        });
      }
    } else {
      el.pause();
    }
  }, [el]);

  const toggleMute = useCallback(() => {
    if (el) {
      el.muted = !el.muted;
    }
  }, [el]);

  const seek = useCallback(
    (time: number) => {
      if (el) {
        el.currentTime = time;
      }
    },
    [el],
  );

  return {
    isPlaying,
    currentTime,
    duration,
    muted,
    togglePlay,
    toggleMute,
    seek,
  };
}
